import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, LayoutDashboard, LogOut, ChevronDown } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';

export const EBDLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/ebd/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-[#0F172A] antialiased">
      <header className="sticky top-0 z-50 h-16 border-b border-[#E5E7EB] bg-white">
        <div className="mx-auto flex h-full max-w-[1400px] items-center justify-between gap-4 px-6">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2563EB] shadow-sm">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight text-[#0F172A]">EBD</p>
                <p className="truncate text-xs text-[#64748B]">Igreja360</p>
              </div>
            </div>
            <nav className="hidden min-w-0 text-sm text-[#64748B] md:block" aria-label="Migalhas">
              <span className="font-medium text-[#0F172A]">EBD</span>
              <span className="mx-1.5 text-[#94A3B8]">/</span>
              <span>Painel</span>
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {user?.nivel_acesso && (
              <Badge
                variant="outline"
                className={
                  user.nivel_acesso === 'superintendente'
                    ? 'hidden sm:inline-flex border-[#DBEAFE] bg-[#DBEAFE] text-[#2563EB]'
                    : 'hidden sm:inline-flex border-[#E5E7EB] bg-white text-[#64748B]'
                }
              >
                {user.nivel_acesso === 'superintendente' ? 'Superintendente' : 'Professor'}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              className="hidden h-10 rounded-lg border-[#E5E7EB] bg-white px-4 text-sm font-medium text-[#0F172A] hover:bg-slate-50 active:scale-[0.98] sm:inline-flex"
              asChild
            >
              <Link to="/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Painel principal
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex max-w-[200px] items-center gap-2 rounded-lg p-2 text-[#0F172A] transition-colors hover:bg-[#F1F5F9] active:scale-[0.98]"
                  data-testid="ebd-user-menu-trigger"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-[#DBEAFE] text-sm font-medium text-[#2563EB]">
                      {getInitials(user?.nome)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden min-w-0 text-left sm:block">
                    <p className="truncate text-sm font-medium text-[#0F172A]">{user?.nome}</p>
                    <p className="truncate text-xs text-[#64748B]">{user?.email}</p>
                  </div>
                  <ChevronDown size={16} className="hidden shrink-0 text-[#94A3B8] sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild className="sm:hidden">
                  <Link to="/dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Painel principal
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="sm:hidden" />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="ebd-logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1400px] px-6 py-6">
        {children}
      </main>
    </div>
  );
};

export default EBDLayout;
